// JoinRequestPage.tsx
// Finalize screen for the Discord "join through guild" flow (#JoinRequest?token=...).
// The Discord bot wizard collected name/class/spec into a guild_join_draft; here
// the applicant signs in with Discord, reviews the drafted character, sets
// availability with the simple day-part control, and submits the application.
// Submitting creates a real (guildless) character plus a pending join request
// that an officer approves or rejects from the guild dashboard.
import React, { useEffect, useState } from "react";
import { API_URL } from "./config";
import { getCurrentResetWeek, localCellToBitIdx, ResetRule } from "./lib/time";
import { useTimezone } from "./lib/TimezoneContext";
import { requestOnboardingTour } from "./OnboardingTour";

type DraftPreview = {
  // true = shareable guild link (applicant supplies their own character here);
  // false/absent = per-person draft from the /ralli join wizard.
  open?: boolean;
  guild: {
    name: string;
    tag: string | null;
    serverName: string | null;
    faction: string | null;
    region: string | null;
    logoUrl: string | null;
    gameId?: string | null;
    /** Reset rule for the guild's region in this game. Absent from an older server, then EU. */
    resetRule?: ResetRule;
  };
  character: {
    name: string;
    classKey: string | null;
    classLabel: string | null;
    specLabels: string[];
  };
  expiresAt: string;
};

const CLASS_COLORS: Record<string, string> = {
  warrior: "#C79C6E", paladin: "#F58CBA", hunter: "#ABD473", rogue: "#FFF569",
  priest: "#FFFFFF", shaman: "#0070DE", mage: "#40C7EB", warlock: "#8787ED",
  druid: "#FF7D0A",
};

const AVAIL_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const DAY_PARTS = [
  { label: "Morning", desc: "8am-12pm", start: 16, end: 24 },
  { label: "Afternoon", desc: "12pm-5pm", start: 24, end: 34 },
  { label: "Evening", desc: "7pm-11pm", start: 38, end: 46 },
] as const;
type DayPart = (typeof DAY_PARTS)[number];

function dayPartBitIndices(weekStartIso: string, dayMon0: number, part: DayPart, tz: string): number[] {
  const out: number[] = [];
  for (let halfHour = part.start; halfHour < part.end; halfHour++) {
    out.push(localCellToBitIdx(weekStartIso, dayMon0, halfHour, tz));
  }
  return out;
}

function isPartActive(bits: string, weekStartIso: string, dayMon0: number, part: DayPart, tz: string): boolean {
  return dayPartBitIndices(weekStartIso, dayMon0, part, tz).some((i) => bits[i] === "1");
}

function togglePart(bits: string, weekStartIso: string, dayMon0: number, part: DayPart, tz: string): string {
  const chars = bits.split("");
  const idxs = dayPartBitIndices(weekStartIso, dayMon0, part, tz);
  const active = idxs.filter((i) => chars[i] === "1").length;
  const turnOn = active < idxs.length / 2;
  for (const i of idxs) chars[i] = turnOn ? "1" : "0";
  return chars.join("");
}

const ERROR_COPY: Record<string, string> = {
  invalid_token: "This join link is not valid. Run /ralli join in Discord to get a fresh one.",
  draft_expired: "This join link has expired. Run /ralli join in Discord to get a fresh one.",
  draft_used: "This join link was already used. If you submitted an application, check your roster.",
  guild_inactive: "This guild is no longer active.",
  already_pending: "You already have a pending application to this guild. An officer will review it.",
  discord_mismatch: "You're signed in with a different Discord account than the one that created this link. Sign out, then sign in with the Discord account you used in the guild's server.",
  realm_mismatch: "This character's realm doesn't match the guild's server.",
  guild_preset_missing: "This guild has no realm/faction/region configured. Ask an officer for their regular invite link instead.",
  bad_draft: "This join link contains invalid data. Run /ralli join in Discord to get a fresh one.",
  already_member: "That character is already in this guild.",
};

// Open links are filled in BEFORE signing in, so the half-built character has
// to survive the Discord OAuth round trip. Same trick the trial replay uses.
const DRAFT_KEY = "pending_join_request_draft";
type LocalChar = { name: string; classOptId: number | null; specIds: number[]; interestIds?: number[] };
const saveLocalChar = (c: LocalChar) => {
  try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(c)); } catch {}
};
const loadLocalChar = (): LocalChar | null => {
  try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; }
};

export default function JoinRequestPage({ token, me, onDone }: {
  token: string;
  me: any;
  onDone: () => void;
}) {
  const timezone = useTimezone();
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<DraftPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // An existing member adding a character (e.g. for WoW Forever) is added
  // straight away instead of filing an application.
  const [addedDirectly, setAddedDirectly] = useState(false);

  // The guild region's current week: a US applicant on a Tuesday after reset
  // is already in the next week, which the EU default would miss.
  const weekStart = getCurrentResetWeek(draft?.guild.resetRule);
  const [availBits, setAvailBits] = useState<string>("0".repeat(336));
  const [availLoaded, setAvailLoaded] = useState(false);

  // --- Open-link character form (unused for per-person Discord drafts) ---
  const [fields, setFields] = useState<any[]>([]);
  const restored = loadLocalChar();
  const [charName, setCharName] = useState(restored?.name || "");
  const [classOptId, setClassOptId] = useState<number | null>(restored?.classOptId ?? null);
  const [specIds, setSpecIds] = useState<number[]>(restored?.specIds || []);
  const [interestIds, setInterestIds] = useState<number[] | null>(restored?.interestIds ?? null);
  // Set when the server finds they already own a character with this name.
  const [dupOffer, setDupOffer] = useState<
    { characterId: string; name: string; className: string | null; specLabel: string | null } | null
  >(null);

  const isOpen = !!draft?.open;
  useEffect(() => {
    if (isOpen) saveLocalChar({ name: charName, classOptId, specIds, interestIds: interestIds ?? [] });
  }, [isOpen, charName, classOptId, specIds, interestIds]);

  // Game option groups, for the class/spec pickers.
  useEffect(() => {
    if (!isOpen || !draft?.guild.gameId) return;
    let alive = true;
    fetch(`${API_URL}/games/${draft.guild.gameId}/fields`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (alive) setFields(Array.isArray(d) ? d : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [isOpen, draft?.guild.gameId]);

  const classGroup = fields.find((g: any) => g.key === "class");
  const specGroup = fields.find((g: any) => g.key === "spec");
  const chosenClass = classGroup?.options?.find((o: any) => o.id === classOptId) || null;
  // Specs are class-scoped via allowed_class_keys, same rule the server enforces.
  // NOTE: /games/:id/fields renames the option's value_key to `key`, so reading
  // `value_key` here yields undefined and silently filters every spec away.
  const chosenClassKey = chosenClass?.key || chosenClass?.value_key || null;
  const availableSpecs = (specGroup?.options || []).filter((o: any) => {
    const allowed = o.metadata?.allowed_class_keys || o.metadata?.allowedClassKeys;
    return !allowed || (chosenClassKey && allowed.includes(chosenClassKey));
  });
  const interestGroup = fields.find((g: any) => g.key === "raid_interest");
  const interestOptions = interestGroup?.options || [];
  // Default to every raid ticked. An application that skips this reads as
  // "interested in nothing" to ARC, so opting OUT should be the deliberate act.
  useEffect(() => {
    if (interestIds === null && interestOptions.length > 0) {
      setInterestIds(interestOptions.map((o: any) => o.id));
    }
  }, [interestIds, interestOptions]);
  const pickedInterests = interestIds ?? [];

  const charReady =
    charName.trim().length >= 2 && charName.trim().length <= 24 && !!classOptId && specIds.length > 0
    && (interestOptions.length === 0 || pickedInterests.length > 0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`/api/discord/join-draft/${encodeURIComponent(token)}`);
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) {
          setError(ERROR_COPY[data.error] || "Could not load this join link.");
          return;
        }
        setDraft(data);
      } catch {
        if (alive) setError("Could not load this join link. Check your connection and try again.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  // Prefill availability from the account once signed in.
  useEffect(() => {
    if (!me) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/me/availability", { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        const slots = data?.currentWeek?.slots || data?.slots;
        if (alive && typeof slots === "string" && slots.length > 0) {
          setAvailBits(slots.padEnd(336, "0").slice(0, 336));
        }
      } catch {}
      finally { if (alive) setAvailLoaded(true); }
    })();
    return () => { alive = false; };
  }, [me]);

  const handleLogin = () => {
    sessionStorage.setItem("pending_join_request_token", token);
    window.location.href = `${API_URL}/auth/discord`;
  };

  const handleSubmit = async (mergeCharacterId?: string) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (availBits.includes("1")) {
        await fetch("/me/availability", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          // applyToNextWeek: next week is pre-filled + confirmed with the same times
          body: JSON.stringify({ slots: availBits, weekStart, applyToNextWeek: true, confirm: true }),
        }).catch(() => {});
      }
      const res = await fetch("/guilds/join-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          token,
          ...(isOpen
            ? {
                character: {
                  name: charName.trim(),
                  class_option_id: classOptId,
                  spec_option_ids: specIds,
                  raid_interest_option_ids: pickedInterests,
                },
                ...(mergeCharacterId ? { mergeCharacterId } : {}),
              }
            : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Not an error so much as a fork in the road: they already own this
        // character, so offer to reuse it instead of making a second copy.
        if (data.error === "duplicate_character" && data.duplicate) {
          setDupOffer(data.duplicate);
          return;
        }
        setSubmitError(data.message || ERROR_COPY[data.error] || "Something went wrong. Try again.");
        return;
      }
      sessionStorage.removeItem("pending_join_request_token");
      sessionStorage.removeItem(DRAFT_KEY);
      setDupOffer(null);
      setAddedDirectly(data.added === true);
      setSubmitted(true);
      // Same one-time tour the invite link queues. It waits as a pill and
      // starts once they land on the Roster ("Go to my roster" goes there).
      requestOnboardingTour(charName.trim() || draft?.character?.name || undefined);
    } catch {
      setSubmitError("Something went wrong. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const classColor = draft?.character.classKey
    ? CLASS_COLORS[draft.character.classKey] || "#A3A3A3"
    : "#A3A3A3";

  return (
    <div className="min-h-[70vh] flex items-start justify-center px-4 pt-6 pb-16">
      <div className="w-full max-w-lg">
        {loading && (
          <div className="text-center text-white/40 text-sm animate-pulse py-20">Loading your application...</div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-red-500/20 bg-red-500/5 p-6 text-center">
            <div className="text-3xl mb-3">⚠️</div>
            <div className="text-sm text-white/80">{error}</div>
          </div>
        )}

        {!loading && !error && draft && submitted && (
          <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-8 text-center">
            <div className="text-4xl mb-4">{addedDirectly ? "✅" : "📨"}</div>
            <h2 className="text-xl font-bold text-white mb-2">{addedDirectly ? "Character added!" : "Application sent!"}</h2>
            {addedDirectly ? (
              <p className="text-sm text-white/60 mb-6">
                <span className="font-semibold" style={{ color: classColor }}>{draft.character.name}</span>{" "}
                is now in <span className="text-amber-300 font-semibold">{draft.guild.name}</span>. You were
                already a member, so no approval was needed.
              </p>
            ) : (
            <p className="text-sm text-white/60 mb-6">
              <span className="font-semibold" style={{ color: classColor }}>{draft.character.name}</span>{" "}
              has applied to <span className="text-amber-300 font-semibold">{draft.guild.name}</span>.
              An officer will review it. You'll get a Discord DM with the verdict, and you can track or
              withdraw the application from your roster.
            </p>
            )}
            <button
              onClick={onDone}
              className="px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold transition-colors"
            >
              Go to my roster
            </button>
          </div>
        )}

        {!loading && !error && draft && !submitted && (
          <div className="space-y-4">
            {/* Guild header */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex items-center gap-4">
              {draft.guild.logoUrl ? (
                <img src={draft.guild.logoUrl} alt="" className="w-14 h-14 rounded-xl border border-white/10 object-cover bg-black" />
              ) : (
                <div className="w-14 h-14 rounded-xl border border-white/10 bg-gradient-to-br from-amber-500/20 to-orange-600/10 flex items-center justify-center text-xl font-bold text-amber-300">
                  {draft.guild.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-bold">Guild application</div>
                <div className="text-lg font-bold text-white truncate">{draft.guild.name}</div>
                <div className="text-xs text-white/40">
                  {[draft.guild.serverName, draft.guild.faction, draft.guild.region].filter(Boolean).join(" · ")}
                </div>
              </div>
            </div>

            {/* Open link: they build the character here, before signing in. */}
            {isOpen && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
                <div className="text-[10px] uppercase tracking-widest text-white/35 font-bold">Your character</div>

                <div>
                  <label className="block text-[11px] text-white/45 mb-1.5">Character name</label>
                  <input
                    value={charName}
                    onChange={(e) => { setCharName(e.target.value); setDupOffer(null); }}
                    maxLength={24}
                    placeholder="Exactly as it appears in game"
                    className="w-full px-3 py-2.5 rounded-xl bg-black/30 border border-white/10 focus:border-amber-400/50 focus:outline-none text-sm text-white placeholder-white/25"
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-white/45 mb-1.5">Class</label>
                  <div className="flex flex-wrap gap-1.5">
                    {(classGroup?.options || []).map((o: any) => {
                      const sel = classOptId === o.id;
                      const col = CLASS_COLORS[o.key || o.value_key] || "#A3A3A3";
                      return (
                        <button
                          key={o.id}
                          onClick={() => { setClassOptId(o.id); setSpecIds([]); setDupOffer(null); }}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors"
                          style={sel
                            ? { color: col, borderColor: `${col}66`, background: `${col}1f` }
                            : { color: "rgba(255,255,255,0.5)", borderColor: "rgba(255,255,255,0.1)" }}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                    {!classGroup && <span className="text-xs text-white/25">Loading classes...</span>}
                  </div>
                </div>

                {chosenClass && (
                  <div>
                    <label className="block text-[11px] text-white/45 mb-1.5">
                      Specs you play <span className="text-white/25">(first pick is your main)</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {availableSpecs.map((o: any) => {
                        const idx = specIds.indexOf(o.id);
                        const sel = idx !== -1;
                        return (
                          <button
                            key={o.id}
                            onClick={() => {
                              setDupOffer(null);
                              setSpecIds(prev => prev.includes(o.id) ? prev.filter(x => x !== o.id) : [...prev, o.id]);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                              sel ? "bg-amber-500/15 border-amber-400/40 text-amber-200"
                                  : "bg-white/[0.03] border-white/10 text-white/45 hover:border-white/25"
                            }`}
                          >
                            {idx === 0 && <span className="mr-1 text-[9px] text-amber-400">MAIN</span>}
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {interestOptions.length > 0 && (
                  <div>
                    <div className="flex items-baseline justify-between gap-2 mb-1.5">
                      <label className="text-[11px] text-white/45">
                        Raids you want to be in for
                      </label>
                      <button
                        onClick={() =>
                          setInterestIds(
                            pickedInterests.length === interestOptions.length
                              ? []
                              : interestOptions.map((o: any) => o.id)
                          )
                        }
                        className="text-[10px] font-bold text-white/35 hover:text-white/70 transition-colors"
                      >
                        {pickedInterests.length === interestOptions.length ? "Clear all" : "Select all"}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {interestOptions.map((o: any) => {
                        const sel = pickedInterests.includes(o.id);
                        return (
                          <button
                            key={o.id}
                            onClick={() =>
                              setInterestIds(
                                sel ? pickedInterests.filter(x => x !== o.id) : [...pickedInterests, o.id]
                              )
                            }
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                              sel ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200"
                                  : "bg-white/[0.03] border-white/10 text-white/40 hover:border-white/25"
                            }`}
                          >
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-1.5 text-[11px] text-white/30">
                      Raid leaders only roster you for raids you tick here. Leave them all on if you're flexible.
                    </div>
                  </div>
                )}

                <div className="text-[11px] text-white/30">
                  Realm, faction and region come from the guild ({[draft.guild.serverName, draft.guild.faction].filter(Boolean).join(" · ")}).
                </div>
              </div>
            )}

            {/* Drafted character (Discord wizard already collected it) */}
            {!isOpen && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="text-[10px] uppercase tracking-widest text-white/35 font-bold mb-3">Your character</div>
              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center text-base font-black border"
                  style={{ color: classColor, borderColor: `${classColor}44`, background: `${classColor}14` }}
                >
                  {draft.character.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-base font-bold" style={{ color: classColor }}>{draft.character.name}</div>
                  <div className="text-xs text-white/55">
                    {draft.character.classLabel}
                    {draft.character.specLabels.length > 0 && (
                      <span className="text-white/35"> · {draft.character.specLabels.join(", ")}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-3 text-[11px] text-white/30">
                Realm, faction and region come from the guild. Need something different? Ask an officer for their invite link.
              </div>
            </div>
            )}

            {!me && (
              <div className="rounded-2xl border border-amber-500/25 bg-amber-500/5 p-6 text-center">
                <p className="text-sm text-white/70 mb-4">
                  {isOpen
                    ? "Last step: sign in with Discord to send your application. What you filled in above is kept."
                    : "Sign in with the same Discord account you used in the guild's server to submit this application."}
                </p>
                <button
                  onClick={handleLogin}
                  disabled={isOpen && !charReady}
                  className="px-6 py-2.5 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold transition-colors"
                >
                  Sign in with Discord
                </button>
                {isOpen && !charReady && (
                  <div className="mt-2 text-[11px] text-white/30">Fill in your name, class and at least one spec first.</div>
                )}
              </div>
            )}

            {me && (
              <>
                {/* Availability day-part picker */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="text-[10px] uppercase tracking-widest text-white/35 font-bold mb-1">When can you raid?</div>
                  <div className="text-[11px] text-white/30 mb-3">Tap the blocks that fit your week. Raid leaders use this to schedule you. This week and next week are confirmed with the same times.</div>
                  {!availLoaded ? (
                    <div className="text-xs text-white/30 animate-pulse py-6 text-center">Loading availability...</div>
                  ) : (
                    <div className="space-y-1.5">
                      {AVAIL_DAYS.map((day, dIdx) => (
                        <div key={day} className="flex items-center gap-1.5">
                          <div className="w-9 text-[11px] font-bold text-white/45">{day}</div>
                          <div className="flex-1 grid grid-cols-3 gap-1.5">
                            {DAY_PARTS.map((part) => {
                              const active = isPartActive(availBits, weekStart, dIdx, part, timezone);
                              return (
                                <button
                                  key={part.label}
                                  onClick={() => setAvailBits(togglePart(availBits, weekStart, dIdx, part, timezone))}
                                  className={`py-1.5 rounded-lg text-[10px] font-semibold border transition-colors ${
                                    active
                                      ? "bg-emerald-500/20 border-emerald-400/40 text-emerald-300"
                                      : "bg-white/[0.03] border-white/10 text-white/35 hover:border-white/25"
                                  }`}
                                >
                                  {part.label}
                                  <span className="hidden sm:inline text-[9px] font-normal opacity-60"> {part.desc}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {submitError && (
                  <div className="rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    {submitError}
                  </div>
                )}

                {dupOffer && (
                  <div className="rounded-2xl border border-amber-400/30 bg-amber-500/[0.07] p-5">
                    <div className="text-sm font-bold text-amber-200 mb-1">You already have this character</div>
                    <p className="text-[13px] text-white/60 mb-1">
                      <span className="font-semibold text-white/85">{dupOffer.name}</span>
                      {dupOffer.className && <span className="text-white/40"> · {[dupOffer.specLabel, dupOffer.className].filter(Boolean).join(" ")}</span>}
                      {" "}is already on your account.
                    </p>
                    <p className="text-[11px] text-white/35 mb-4">
                      Use it instead of making a second copy. Its raid history and parses are kept, and the
                      class and specs you just picked replace what was there.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleSubmit(dupOffer.characterId)}
                        disabled={submitting}
                        className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-xs font-bold transition-colors"
                      >
                        {submitting ? "Applying..." : `Use ${dupOffer.name} and apply`}
                      </button>
                      <button
                        onClick={() => setDupOffer(null)}
                        className="px-4 py-2 rounded-xl bg-white/[0.05] hover:bg-white/10 border border-white/10 text-white/60 text-xs font-bold transition-colors"
                      >
                        Change the name instead
                      </button>
                    </div>
                  </div>
                )}

                {!dupOffer && (
                <button
                  onClick={() => handleSubmit()}
                  disabled={submitting || (isOpen && !charReady)}
                  className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-sm font-bold transition-colors"
                >
                  {submitting ? "Sending application..." : `Apply to ${draft.guild.name}`}
                </button>
                )}
                <div className="text-center text-[11px] text-white/30">
                  An officer reviews every application. You'll get a Discord DM with the verdict.
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
